import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AdminLayout from './AdminLayout';
import AdminLogin from './pages/AdminLogin';
import Dashboard from './pages/Dashboard';
import UserList from './pages/UserList';
import UserDetail from './pages/UserDetail';
import QdrantManagement from './pages/QdrantManagement';
import SystemLogs from './pages/SystemLogs';
import AdminUsers from './pages/AdminUsers';
import AgentSettings from './pages/AgentSettings';
import SystemSettings from './pages/SystemSettings';
import ApiKeysSettings from './pages/ApiKeysSettings';
import PublicationSlots from './pages/PublicationSlots';
import TestPublish from './pages/TestPublish';
import TestPublishManual from './pages/TestPublishManual';
import SocialAccounts from './pages/SocialAccounts';
import ScheduledVideos from './pages/ScheduledVideos';
import DatabaseBackups from './pages/DatabaseBackups';
import SystemAlerts from './pages/SystemAlerts';
import OrphanComments from './pages/OrphanComments';
import MonitoringDashboard from './pages/MonitoringDashboard';
import MonitoringConversations from './pages/MonitoringConversations';
import MonitoringConversationDetail from './pages/MonitoringConversationDetail';

// Admin Route Component - Requires admin authentication
function AdminRoute({ children, requireSuperAdmin = false }) {
  const location = useLocation();
  const { isAuthenticated, user, initializing } = useSelector((state) => state.auth);
  
  // Show loading while initializing
  if (initializing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }
  
  // Redirect to admin login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  
  // Check admin role
  if (!['admin', 'superadmin'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  // Check superadmin requirement
  if (requireSuperAdmin && user.role !== 'superadmin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  
  return (
    <AdminLayout>
      {children}
    </AdminLayout>
  );
}

function AdminApp() {
  return (
    <Routes>
      <Route path="login" element={<AdminLogin />} />
      <Route 
        path="dashboard" 
        element={
          <AdminRoute>
            <Dashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="users" 
        element={
          <AdminRoute>
            <UserList />
          </AdminRoute>
        } 
      />
      <Route 
        path="users/:userId" 
        element={
          <AdminRoute>
            <UserDetail />
          </AdminRoute>
        } 
      />
      <Route 
        path="logs" 
        element={
          <AdminRoute>
            <SystemLogs />
          </AdminRoute>
        } 
      />
      <Route 
        path="admins" 
        element={
          <AdminRoute requireSuperAdmin={true}>
            <AdminUsers />
          </AdminRoute>
        } 
      />
      <Route 
        path="qdrant" 
        element={
          <AdminRoute requireSuperAdmin={true}>
            <QdrantManagement />
          </AdminRoute>
        } 
      />
      <Route 
        path="settings/agents" 
        element={
          <AdminRoute requireSuperAdmin={true}>
            <AgentSettings />
          </AdminRoute>
        } 
      />
      <Route 
        path="settings/system" 
        element={
          <AdminRoute requireSuperAdmin={true}>
            <SystemSettings />
          </AdminRoute>
        } 
      />
      <Route 
        path="settings/api-keys" 
        element={
          <AdminRoute requireSuperAdmin={true}>
            <ApiKeysSettings />
          </AdminRoute>
        } 
      />
      <Route 
        path="publication-slots" 
        element={
          <AdminRoute>
            <PublicationSlots />
          </AdminRoute>
        } 
      />
      <Route 
        path="social-accounts" 
        element={
          <AdminRoute>
            <SocialAccounts />
          </AdminRoute>
        } 
      />
      <Route 
        path="scheduled-videos" 
        element={
          <AdminRoute>
            <ScheduledVideos />
          </AdminRoute>
        } 
      />
      <Route
        path="test-publish"
        element={
          <AdminRoute requireSuperAdmin={true}>
            <TestPublish />
          </AdminRoute>
        }
      />
      <Route
        path="test-publish/manual"
        element={
          <AdminRoute requireSuperAdmin={true}>
            <TestPublishManual />
          </AdminRoute>
        }
      />
      <Route 
        path="backups" 
        element={
          <AdminRoute requireSuperAdmin={true}>
            <DatabaseBackups />
          </AdminRoute>
        } 
      />
      <Route
        path="alerts"
        element={
          <AdminRoute requireSuperAdmin={true}>
            <SystemAlerts />
          </AdminRoute>
        }
      />
      <Route
        path="orphan-comments"
        element={
          <AdminRoute requireSuperAdmin={true}>
            <OrphanComments />
          </AdminRoute>
        }
      />
      <Route
        path="monitoring"
        element={
          <AdminRoute>
            <MonitoringDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="monitoring/conversations"
        element={
          <AdminRoute>
            <MonitoringConversations />
          </AdminRoute>
        }
      />
      <Route
        path="monitoring/conversations/:conversationId"
        element={
          <AdminRoute>
            <MonitoringConversationDetail />
          </AdminRoute>
        }
      />
      <Route path="" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  );
}

export default AdminApp;

